import React from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  FormGroup,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import useCache from "../../app/useCache";
import { CachedEntity } from "../../app/cacheSlice";

type OrganizationApplicationsPanelProps = {
  value: number[] | null; // null => still loading
  onChange: (applicationIds: number[]) => void;
};

const OrganizationApplicationsPanel: React.FC<OrganizationApplicationsPanelProps> = ({ value, onChange }) => {
  // Unfiltered master list — safe because this panel only renders for TenantAdmins.
  const applications = useCache(CachedEntity.Application);

  const isLoading = value === null;

  const handleToggle = (applicationId: number, enabled: boolean) => {
    const current = value ?? [];
    onChange(enabled ? [...current, applicationId] : current.filter((id) => id !== applicationId));
  };

  return (
    <React.Fragment>
      <Typography variant="h5" color="primary" sx={{ mt: 2 }}>
        Organization Applications
      </Typography>
      {!isLoading && applications.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No applications available.
        </Typography>
      )}
      {!isLoading && applications.length > 0 && value!.length === 0 && (
        <Alert severity="warning" sx={{ my: 1 }}>
          No applications are enabled for this organization. Users in this organization will not see any applications.
        </Alert>
      )}
      <FormGroup>
        {applications.map((application) => (
          <Box key={application.key} mb={1}>
            <FormControlLabel
              control={
                <Switch
                  name={`applications.${application.id}`}
                  checked={value?.includes(application.id) ?? false}
                  onChange={(_, checked) => handleToggle(application.id, checked)}
                  disabled={isLoading}
                />
              }
              label={
                <Box display="flex" alignItems="center">
                  {application.label ? application.label : application.key}
                  <Tooltip title={application.description} placement="right">
                    <InfoIcon color="inherit" fontSize="small" sx={{ ml: 0.5 }} />
                  </Tooltip>
                </Box>
              }
            />
          </Box>
        ))}
      </FormGroup>
    </React.Fragment>
  );
};

export default OrganizationApplicationsPanel;
